import { useMatches, useLocation } from 'react-router-dom'

import { BreadcrumbSegment, RouteHandle } from '@/types/layout'

/**
 * Custom hook to generate breadcrumb segments from React Router matches
 * Extracts breadcrumb configuration from route handle and builds full path hierarchy
 */
export const useBreadcrumb = (): BreadcrumbSegment[] => {
  const matches = useMatches()
  const location = useLocation()
  
  // Build breadcrumb segments from path hierarchy
  const breadcrumbs: BreadcrumbSegment[] = []
  
  // Always add Dashboard first (unless we're on login page)
  if (!location.pathname.startsWith('/login')) {
    breadcrumbs.push({
      label: 'Dashboard',
      path: '/dashboard',
      isActive: false,
    })
  }
  
  // Find the current route match with breadcrumb
  const currentMatch = matches.find((match) => {
    const handle = match.handle as RouteHandle | undefined
    return handle && handle.breadcrumb && !handle.hideBreadcrumb
  })
  
  if (currentMatch) {
    const handle = currentMatch.handle as RouteHandle
    const params = currentMatch.params as Record<string, string>
    const pathname = currentMatch.pathname
    
    // Parse path to build hierarchy
    const pathParts = pathname.split('/').filter(Boolean)
    
    // For gamification routes, add intermediate breadcrumbs
    if (pathParts.includes('gamification')) {
      // Add Gamification breadcrumb if not on the base missions page
      if (pathname !== '/gamification/missions') {
        breadcrumbs.push({
          label: 'Quản lý Gamification',
          path: '/gamification/missions',
          isActive: false,
        })
      }
    }
    
    // For user-management routes, add intermediate breadcrumbs
    if (pathParts.includes('user-management')) {
      // Add "Quản lý Người dùng" breadcrumb
      breadcrumbs.push({
        label: 'Quản lý Người dùng',
        path: '/user-management/users',
        isActive: false,
      })
      
      // If we're in the users section (not admins), add "Người dùng" breadcrumb
      if (pathParts.includes('users') && pathname !== '/user-management/users') {
        breadcrumbs.push({
          label: 'Người dùng',
          path: '/user-management/users',
          isActive: false,
        })
      }
    }
    
    // Add the current page breadcrumb
    const label = typeof handle.breadcrumb === 'function' 
      ? handle.breadcrumb(params)
      : handle.breadcrumb || ''
    
    breadcrumbs.push({
      label,
      path: pathname,
      isActive: true,
    })
  }
  
  return breadcrumbs
}
